import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar
} from '@carbon/react';

import { routes } from 'core/routing';
import Link from 'shared/components/Link';
import ProjectTier, { ProjectTierTooltip } from 'shared/components/ProjectTier';
import ProjectListTableToolbar from 'shared/project-list/ProjectListTableToolbar';
import { formatDate } from 'utils/dates';
import styles from 'theme/features/landing/landing.module.scss';

const RecentProjects = ({ projects }) => {
  const rows =
    projects?.map((x) => ({
      id: x.id.toString(),
      location: `${x.city}, ${x.country}`,
      name: <Link href={routes.projectOutputs(x.id)}>{x.name}</Link>,
      tier: x.tier != null && <ProjectTier tier={x.tier} isValid={x.tierIsValid} />,
      createdOn: formatDate(x.createdOn),
      createdBy: x.createdBy,
      updatedOn: x.updatedOn ? formatDate(x.updatedOn) : '-',
      updatedBy: x.updatedBy ?? '-',
      label:
        x.isFinal && x.isConfidential ? (
          <span className={styles.label}>Final Version | Confidential</span>
        ) : x.isFinal ? (
          <span className={styles.label}>Final Version</span>
        ) : x.isConfidential ? (
          <span className={styles.label}>Confidential</span>
        ) : (
          ''
        )
    })) ?? [];

  const headers = [
    { key: 'name', header: 'Project Name' },
    { key: 'label', header: '' },
    {
      key: 'tier',
      header: (
        <>
          Tier
          <span style={{ top: 0, position: 'relative' }}>
            <ProjectTierTooltip />
          </span>
        </>
      )
    },
    { key: 'location', header: 'Location' },
    { key: 'createdOn', header: 'Created On' },
    { key: 'createdBy', header: 'Created By' },
    { key: 'updatedOn', header: 'Modified On' },
    { key: 'updatedBy', header: 'Modified By' }
  ];

  return (
    <DataTable
      headers={headers}
      rows={rows}
      size="sm"
      render={({
        rows,
        getTableContainerProps,
        getToolbarProps,
        getTableProps,
        getSelectionProps,
        getHeaderProps,
        getRowProps,
        selectAll,
        selectedRows
      }) => (
        <TableContainer
          title="Your Recent Projects"
          description="The most recent projects you've interacted with. You can view the full list of your projects through 'All Projects' in the top menu"
          className={styles['table-header']}
          {...getTableContainerProps()}
        >
          <TableToolbar {...getToolbarProps()} size="sm">
            <ProjectListTableToolbar selectAll={selectAll} selectedRows={selectedRows} />
          </TableToolbar>
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                <TableSelectAll {...getSelectionProps()} />
                {headers.map((header) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow {...getRowProps({ row })} key={row.id}>
                  <TableSelectRow {...getSelectionProps({ row })} />
                  {row.cells.map((cell) => (
                    <TableCell key={cell.id}>{cell.value}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    />
  );
};

export default RecentProjects;
